import { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertCircle, Check, Loader2, Pencil, Plus, UserCheck, UserX, Users } from 'lucide-react';
import { api } from '../services/api';
import { useAuthStore } from '../stores/authStore';

interface UserItem {
  id: number;
  email: string;
  name: string;
  role: string;
  isActive: boolean;
  createdAt: string;
}

function errorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError<{ error?: string }>(err) && err.response?.data?.error) {
    return err.response.data.error;
  }
  return fallback;
}

export default function UsersPage() {
  const [users, setUsers] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserItem | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [pendingDeactivate, setPendingDeactivate] = useState<UserItem | null>(null);
  const [busyUserId, setBusyUserId] = useState<number | null>(null);
  const currentUser = useAuthStore((s) => s.user);

  const [form, setForm] = useState({
    email: '',
    password: '',
    name: '',
    role: 'teacher' as 'admin' | 'teacher',
  });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/users');
      setUsers(res.data.data);
    } catch (err: unknown) {
      setError(errorMessage(err, 'Не удалось загрузить список пользователей'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const openCreate = () => {
    setEditingUser(null);
    setForm({ email: '', password: '', name: '', role: 'teacher' });
    setError('');
    setShowModal(true);
  };

  const openEdit = (user: UserItem) => {
    setEditingUser(user);
    setForm({ email: user.email, password: '', name: user.name, role: user.role as 'admin' | 'teacher' });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');
    setNotice('');
    try {
      if (editingUser) {
        const payload: Partial<typeof form> = { ...form };
        // The server validates `password` with a 6-character minimum, so a blank
        // field must be omitted entirely — sending "" would fail with 400.
        if (!payload.password) delete payload.password;
        await api.patch(`/users/${editingUser.id}`, payload);
        setNotice(
          payload.password
            ? 'Пользователь обновлён, пароль изменён.'
            : 'Пользователь обновлён.',
        );
      } else {
        await api.post('/users', form);
        setNotice('Пользователь создан.');
      }
      setShowModal(false);
      fetchUsers();
    } catch (err: unknown) {
      setError(errorMessage(err, 'Не удалось сохранить пользователя'));
    } finally {
      setIsSaving(false);
    }
  };

  const setActive = async (user: UserItem, isActive: boolean) => {
    setBusyUserId(user.id);
    setError('');
    setNotice('');
    try {
      await api.patch(`/users/${user.id}`, { isActive });
      setNotice(
        isActive ? `${user.name} снова активен.` : `${user.name} деактивирован — вход заблокирован.`
      );
      fetchUsers();
    } catch (err: unknown) {
      setError(errorMessage(err, 'Не удалось изменить статус пользователя'));
    } finally {
      setBusyUserId(null);
    }
  };

  // DELETE is a soft delete on the server: the row stays, only `isActive` flips.
  const confirmDeactivate = async () => {
    if (!pendingDeactivate) return;
    const user = pendingDeactivate;
    setPendingDeactivate(null);
    await setActive(user, false);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Users size={20} className="text-brand-600" />
          <h1 className="text-2xl font-bold text-gray-900">Пользователи</h1>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 self-start rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          <Plus size={16} />
          Добавить учителя
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {notice && (
        <div className="flex items-start gap-2 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700">
          <Check size={16} className="mt-0.5 shrink-0" />
          <span>{notice}</span>
        </div>
      )}

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-4 py-3 font-medium">Имя</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Роль</th>
                <th className="px-4 py-3 font-medium">Статус</th>
                <th className="px-4 py-3 font-medium">Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((user) => {
                const isSelf = user.id === currentUser?.id;
                const busy = busyUserId === user.id;
                return (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {user.name}
                      {isSelf && <span className="ml-2 text-xs text-gray-400">(это вы)</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{user.email}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                          user.role === 'admin'
                            ? 'bg-purple-50 text-purple-700'
                            : 'bg-blue-50 text-blue-700'
                        }`}
                      >
                        {user.role === 'admin' ? 'Администратор' : 'Учитель'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                          user.isActive
                            ? 'bg-green-50 text-green-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {user.isActive ? 'Активен' : 'Деактивирован'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEdit(user)}
                          title="Редактировать"
                          aria-label="Редактировать"
                          className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-brand-600"
                        >
                          <Pencil size={16} />
                        </button>
                        {!isSelf && (
                          <button
                            onClick={() =>
                              user.isActive
                                ? setPendingDeactivate(user)
                                : setActive(user, true)
                            }
                            disabled={busy}
                            title={user.isActive ? 'Деактивировать' : 'Активировать'}
                            aria-label={user.isActive ? 'Деактивировать' : 'Активировать'}
                            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-brand-600 disabled:opacity-50"
                          >
                            {busy ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : user.isActive ? (
                              <UserX size={16} />
                            ) : (
                              <UserCheck size={16} />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-gray-400">
        Деактивация закрывает вход, но не удаляет пользователя: его темы, слова и тесты остаются.
      </p>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">
              {editingUser ? 'Редактировать пользователя' : 'Новый пользователь'}
            </h2>
            {error && (
              <div className="mb-4 flex items-start gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Имя</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Пароль {editingUser && '(оставьте пустым, чтобы не менять)'}
                </label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  minLength={6}
                  placeholder="Минимум 6 символов"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  {...(!editingUser && { required: true })}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Роль</label>
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value as 'admin' | 'teacher' })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                >
                  <option value="teacher">Учитель</option>
                  <option value="admin">Администратор</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {isSaving && <Loader2 size={16} className="animate-spin" />}
                  {editingUser ? 'Сохранить' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {pendingDeactivate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Деактивировать пользователя?</h3>
            <p className="mt-2 text-sm text-gray-500">
              «{pendingDeactivate.name}» ({pendingDeactivate.email}) больше не сможет войти в систему.
              Его темы, слова и тесты сохранятся, и аккаунт можно будет активировать снова.
            </p>
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setPendingDeactivate(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Отмена
              </button>
              <button
                onClick={confirmDeactivate}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                <UserX size={16} />
                Деактивировать
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}